/** A dropdown you can type into.
 *
 * FKMT, 8 September 2026: *"under all dropdown I want to have a search option
 * instead of scrolling until I find the required item."*
 *
 * The item master will hold thousands of parts. Choosing one from a native
 * `<select>` means scrolling a list the length of the business — and the
 * browser's own type-ahead only matches from the START of the label, so
 * hunting for "12mm MS Plate" by typing "plate" finds nothing at all.
 *
 * SO IT MATCHES ANYWHERE IN THE TEXT, and it matches the VALUE as well as the
 * label — somebody who knows an item as 704000001 should be able to type that
 * rather than remember what it is called.
 *
 * WHEN THE SEARCH BOX APPEARS. Only when the list is long enough to need it —
 * `SEARCH_THRESHOLD` below. Make / Buy / Both is three options and putting a
 * search box over it would be clutter pretending to be helpfulness. The
 * component is the same either way, so nothing behaves differently from screen
 * to screen; only the box is absent.
 *
 * KEYBOARD FIRST, because this is data entry. Stores and purchasing people
 * work down a form without touching the mouse:
 *
 *   type          filters
 *   ↓ ↑           move through the matches
 *   Enter         choose the highlighted one
 *   Escape        close, keeping what was already chosen
 *   Tab           leaves the field, closing the list
 *
 * It is a real combobox in the accessibility sense — `role="combobox"`,
 * `aria-expanded`, `aria-activedescendant` — so a screen reader announces the
 * highlighted option rather than silence.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

/** `group` is optional. When options carry one, a heading is drawn wherever
 *  it changes — ITEM_MASTER.md §10.4: the BOM picker shows parts tagged to
 *  the parent's model first, then EVERYTHING ELSE under a heading that says
 *  so. Headings are not options: the arrow keys pass over them, and a list
 *  without groups renders exactly as it did before they existed. */
export interface Option { value: string; label: string; group?: string }

/** Below this, a search box is noise. Above it, scrolling is the noise. */
export const SEARCH_THRESHOLD = 8

export function SearchSelect(
  { id, value, options, onChange, disabled, placeholder, emptyLabel }: {
    id?: string
    value: string
    options: Option[]
    onChange: (v: string) => void
    disabled?: boolean
    placeholder?: string
    /** What the "nothing chosen" row says. Some fields are optional and some
     *  are not, and "—" is the wrong word for both. */
    emptyLabel?: string
  },
) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const searchable = options.length >= SEARCH_THRESHOLD
  const chosen = options.find(o => o.value === value)

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    // Anywhere in the text, and in the value too — a native select matches
    // only the start of the label, which is why this exists.
    return options.filter(o =>
      o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
  }, [options, query])

  // Close on a click outside. Not on blur: clicking an option blurs the input
  // first, and closing there would swallow the click.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (open && searchable) search.current?.focus()
  }, [open, searchable])

  // Keep the highlighted row in view when the arrows walk past the edge.
  useEffect(() => {
    if (!open) return
    const row = listRef.current?.querySelector('[data-active="true"]')
    // Guarded rather than assumed: `scrollIntoView` is absent in jsdom, and
    // keeping a row visible is a convenience. It must never be the reason a
    // dropdown throws.
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' })
    }
  }, [open, active])

  function choose(o: Option | null) {
    onChange(o ? o.value : '')
    setOpen(false)
    setQuery('')
    setActive(0)
  }

  function onKey(e: React.KeyboardEvent) {
    if (disabled) return
    if (!open) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault()
        setOpen(true)
        setActive(0)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(a => Math.min(a + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(a => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      choose(matches[active] ?? null)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setQuery('')
    } else if (e.key === 'Tab') {
      setOpen(false)
      setQuery('')
    }
  }

  return (
    <div className="ss" ref={box}>
      <button
        type="button"
        id={id}
        className="ss-control"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={id ? `${id}-list` : undefined}
        aria-activedescendant={
          open && matches[active] && id ? `${id}-o-${active}` : undefined}
        disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(o => !o); setActive(0) } }}
        onKeyDown={onKey}
      >
        <span className={chosen ? 'ss-value' : 'ss-placeholder'}>
          {chosen ? chosen.label : (placeholder ?? emptyLabel ?? '—')}
        </span>
        <span className="ss-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="ss-pop">
          {searchable && (
            <input
              ref={search}
              className="ss-search"
              value={query}
              placeholder="Type to search…"
              aria-label="Search the list"
              onChange={e => { setQuery(e.target.value); setActive(0) }}
              onKeyDown={onKey}
            />
          )}

          <ul className="ss-list" role="listbox" ref={listRef}
              id={id ? `${id}-list` : undefined}>
            {/* Clearing is a choice, so it is a row rather than a gesture
                nobody discovers. */}
            <li
              role="option"
              aria-selected={!value}
              className={`ss-option ss-clear${!value ? ' is-chosen' : ''}`}
              onMouseDown={e => { e.preventDefault(); choose(null) }}
            >{emptyLabel ?? '— none —'}</li>

            {matches.map((o, i) => [
              o.group && o.group !== matches[i - 1]?.group ? (
                <li key={`g:${o.group}`} role="presentation"
                    className="ss-group">{o.group}</li>
              ) : null,
              <li
                key={o.value}
                id={id ? `${id}-o-${i}` : undefined}
                role="option"
                aria-selected={o.value === value}
                data-active={i === active}
                className={'ss-option'
                  + (i === active ? ' is-active' : '')
                  + (o.value === value ? ' is-chosen' : '')}
                onMouseEnter={() => setActive(i)}
                onMouseDown={e => { e.preventDefault(); choose(o) }}
              >{o.label}</li>,
            ])}

            {matches.length === 0 && (
              // Never a blank box. UI_TASTE.md — say why a choice list is empty.
              <li className="ss-empty">
                {options.length === 0
                  ? 'Nothing to choose from yet.'
                  : `Nothing matches “${query}”.`}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
